import { FieldValidator, ValidationErrors } from 'final-form'
import { TypeUserSnackbarAction } from '../stores/snackbar/snackbarAction'

export const composeValidators =
    (...validators: FieldValidator<any>[]): FieldValidator<any> =>
    (value, allValues, meta): string | undefined => {
        return validators.reduce(
            (error: string | undefined, validator) => error || validator(value, allValues, meta),
            undefined,
        )
    }

type FieldValidatorCustomErrorMessage<T> = (errorMessage?: string) => FieldValidator<T>

export const textRequired: FieldValidatorCustomErrorMessage<string | number> =
    (errorMessage = 'กรุณากรอกข้อความ') =>
    (value, allValues: any, meta) => {
        const submitType: any = allValues?.submitType
        if (typeof value === 'undefined' || value === null || value === '' || value === '\n') {
            if (submitType === 'draft' && meta?.name !== 'projectNameTH') {
                return null
            } else {
                return errorMessage
            }
        }
    }

export const richEditorRequired: FieldValidatorCustomErrorMessage<string | number> =
    (errorMessage = 'กรุณากรอกข้อความ') =>
    (value, allValues: any, meta) => {
        const submitType: any = allValues?.submitType ?? 'save'
        if (typeof value === 'object') {
            if (
                submitType === 'draft' ||
                (submitType === 'save' &&
                    Object.values(value)[0] !== '<p></p>\n' &&
                    !(Object.values(value)[0] === undefined || Object.values(value)[0] === ''))
            ) {
                return null
            } else {
                return errorMessage
            }
        }
    }

export const selectRequired: FieldValidatorCustomErrorMessage<string | number> =
    (errorMessage = 'กรุณาเลือก') =>
    (value, allValues: any, meta) => {
        const submitType: any = allValues?.submitType
        const emptyVal = JSON.stringify(value) === JSON.stringify({})
        if (typeof value === 'undefined' || value === null || value === '' || emptyVal) {
            if (submitType === 'draft' && meta?.name !== 'IRB') {
                return null
            } else if (submitType === 'saveSing' && (allValues?.chairman || allValues?.secretary)) {
                return null
            } else if (allValues?.chairman || allValues?.secretary) {
                return null
            } else {
                return errorMessage
            }
        }
    }

export const validateEmail: FieldValidatorCustomErrorMessage<string> =
    (errorMessage = 'กรุณากรอกอีเมลให้ถูกต้อง') =>
    (value, allValues, meta) => {
        if (value && !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value)) {
            return errorMessage
        } else {
            return undefined
        }
    }

export const validatePhone: FieldValidatorCustomErrorMessage<string> =
    (errorMessage = 'กรุณากรอกข้อความ') =>
    (value, _allValues, _meta) => {
        return undefined
    }

export const validateMobilePhone: FieldValidatorCustomErrorMessage<string> =
    (errorMessage = 'กรุณากรอกข้อความ') =>
    (value, _allValues, _meta) => {
        return undefined
    }

export const validateTextLength30: FieldValidator<string> = (value, allValues, meta) => {
    if (value && value.length > 30) {
        return 'กรอกข้อความได้ไม่เกิน 30 ตัวอักษร'
    } else {
        return undefined
    }
}

export const validateEN: FieldValidatorCustomErrorMessage<string> =
    (errorMessage = 'กรุณากรอกข้อความ') =>
    (value, allValues, meta) => {
        if (value && /[\u0E00-\u0E7F]/.test(value)) {
            return errorMessage
        } else {
            return undefined
        }
    }

export const uploadRequired: FieldValidatorCustomErrorMessage<Array<File>> =
    (errorMessage = 'กรุณาเลือก') =>
    (value, allValues, meta) => {
        if (typeof value === 'undefined' || value === null || value.length === 0) {
            return errorMessage
        }
    }

export const uploadDocumentRequired: FieldValidatorCustomErrorMessage<{
    fileList: [{ documentTopicID: number; fileID: string; fileToken: string; filename: string }]
    documentTopicID: number
}> =
    (errorMessage = 'กรุณาเลือก') =>
    (value, allValues, meta) => {
        if (typeof value === 'object' && !value.fileList.length) {
            return errorMessage
        }
    }

// validate ค่าเฉพาะตัวเลข แบบไม่มีทศนิยม
export const validateNumeralNoPrecision: FieldValidatorCustomErrorMessage<string> =
    (errorMessage = 'กรุณากรอกเฉพาะตัวเลข') =>
    (value, allValues, meta) => {
        if (value && /^[0-9]*$/gm.test(value)) {
            return undefined
        } else if (!value) {
            return undefined
        } else {
            return errorMessage
        }
    }

export const snackbarMessage = (errors: ValidationErrors, snackbarAction: TypeUserSnackbarAction) => {
    var values =
        errors &&
        (Object.keys(errors) as Array<keyof typeof errors>).reduce(
            (accumulator, current) => {
                accumulator.push(errors[current])
                return accumulator
            },
            [] as (typeof errors)[keyof typeof errors][],
        )
    values &&
        snackbarAction.openSnackbar({
            message: values && values[0],
            type: 'error',
        })
}

export const validateTreeCheckboxRequireAtLeastOne: FieldValidatorCustomErrorMessage<{
    childSelected: { id: number; checked: boolean; name_th: string; name_en: string }[]
    parentSelected: { id: number; checked: boolean; name_th: string; name_en: string }[]
}> =
    (errorMessage = 'กรุณาเลือกอย่างน้อย1ข้อ') =>
    (value) => {
        const isParentSelected = value?.parentSelected?.filter((item) => item.id !== null && item.checked === true)
        const isChildSelected = value?.childSelected?.filter((item) => item.id !== null && item.checked === true)
        //@ts-ignore
        if (value && value.parentSelected) {
            //@ts-ignore
            if (isParentSelected.length > 0 || isChildSelected.length > 0) {
                return undefined
            } else {
                return errorMessage
            }
        }
    }

export const validateModeAccordionError = (names: string[], formProps: any) => {
    let isError = false
    const errKeys = Object.keys(formProps?.errors)
    let isEmptyPage = false
    formProps?.values?.uploadFile?.forEach((item: any) => {
        if (item?.uploadFileObj.documentTopicID === 17) {
            const chkEmpty = item?.uploadFileObj?.subTopicPageList?.length
            if (!chkEmpty) {
                isEmptyPage = true
            }
        } else {
            isEmptyPage = false
        }
    })

    names.forEach((name) => {
        const findError = errKeys.includes(name)
        if (findError || isEmptyPage) {
            isError = true
        }
    })
    return isError
}

export const validateMaxDocLength: FieldValidatorCustomErrorMessage<string> =
    (errorMessage = 'กรุณาตรวจสอบ') =>
    (value, allValues, meta) => {
        let error = undefined
        if (value && value?.length > 3) {
            error = errorMessage
        }
        return error
    }

const chulaEmailRegex = /^[a-z0-9._%+-]+@(?:[a-z0-9-]+\.)*chula\.ac\.th$/i;

export const validateChulaEmail: FieldValidatorCustomErrorMessage<string> =
    (errorMessage = 'กรุณาใช้อีเมล @chula.ac.th ให้ถูกต้อง') =>
    (value, _allValues, _meta) => {
        const v = (value ?? '').trim()
        if (!v) return
        return chulaEmailRegex.test(v) ? undefined : errorMessage
    }
